import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        // Diverged from stock shadcn because: (3) the design system needs a
        // warning semantic to sit between neutral and destructive, for actions
        // that interrupt an automated flow without destroying anything —
        // pausing an order, and the sibling hold types to come. Stock shadcn
        // ships only `destructive`, which reads as "this deletes something" and
        // is the wrong promise for a reversible pause.
        //
        // Amber is the app's existing warning language (see `statusTone.ts`,
        // where every awaiting/attention state is amber); this variant names it
        // rather than inventing a colour, so the button matches the status pill
        // beside it. Explicit light/dark values because `bg-warning/10` style
        // opacity collapses to unreadable dark-amber-on-near-black in dark mode.
        warning:
          'bg-amber-500 text-amber-950 hover:bg-amber-500/90 dark:bg-amber-500 dark:text-amber-950',
        // Diverged from stock shadcn because: (3) the design system needs the
        // quiet form of the warning semantic above, for a warning action offered
        // in a toolbar or header rather than committed to in a dialog. Stock
        // `outline` is the right shape but carries no semantic at all, so a hold
        // would read as a plain secondary action.
        //
        // A solid amber fill dominates a header it shares with neutral controls,
        // which overstates an action most viewers will never take. Amber moves to
        // the label and border instead, matching the restraint the orders-list row
        // menu already applies (warning colour on the item label, never the row).
        //
        // `warning` keeps the fill and stays the right token for the affirmative
        // button in a confirmation dialog, where the choice has already been
        // made and the primary must outrank the Cancel beside it.
        warningOutline:
          'border border-amber-500/60 bg-transparent text-amber-700 hover:bg-amber-50 hover:text-amber-800 dark:border-amber-400/40 dark:text-amber-400 dark:hover:bg-amber-950 dark:hover:text-amber-300',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
        // 36x36, per the Primary Action and Overflow Menu pattern. Distinct from
        // `icon` (40x40) so an icon-only kebab lines up with an `sm` button (h-9)
        // sitting next to it in an action group.
        'icon-sm': 'h-9 w-9',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false,...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = 'Button';
